export const PAGE_SIZE = 20;
export const ALL_SOURCES = 'all';

export const SORTABLE_COLUMNS = ['topic', 'source'];

export const DEFAULT_STATS = {
  totalChunks: 0,
  sources: 0,
  pagesSlidesUrls: 0,
  lastProcessed: null,
};

export const formatLastProcessed = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const filterKnowledgeRows = (rows, { search = '', source = ALL_SOURCES } = {}) => {
  const query = search.trim().toLowerCase();
  return (rows || []).filter((row) => {
    const matchesSource = source === ALL_SOURCES || String(row.sourceId) === String(source);
    if (!matchesSource) return false;
    if (!query) return true;
    return [row.topic, row.source, row.text].some((field) =>
      String(field || '')
        .toLowerCase()
        .includes(query),
    );
  });
};

export const sortKnowledgeRows = (rows, { sortBy = 'topic', sortDir = 'asc' } = {}) => {
  const direction = sortDir === 'desc' ? -1 : 1;
  return [...(rows || [])].sort((a, b) => {
    const left = String(a[sortBy] || '').toLowerCase();
    const right = String(b[sortBy] || '').toLowerCase();
    if (left < right) return -1 * direction;
    if (left > right) return 1 * direction;
    return 0;
  });
};

export const paginateRows = (rows, { page = 1, pageSize = PAGE_SIZE } = {}) => {
  const total = rows?.length || 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize) || 1);
  const currentPage = Math.min(Math.max(1, page), pageCount);
  const start = (currentPage - 1) * pageSize;
  return {
    total,
    pageCount,
    currentPage,
    rows: (rows || []).slice(start, start + pageSize),
  };
};

export const resolveClientSelection = (companies, requestedCompanyId) => {
  const list = companies || [];
  if (list.length === 0) {
    return { selectedClientId: '', selectedCompany: null };
  }
  const match = list.find((company) => String(company._id) === String(requestedCompanyId));
  const selectedCompany = match || list[0];
  return {
    selectedClientId: selectedCompany._id,
    selectedCompany,
  };
};

/** Knowledge is stored against the client User, while the picker lists Companies. */
export const clientUserIdOf = (company) => {
  const client = company?.client;
  if (!client) return '';
  return String(client._id || client);
};

export const getInfoBannerMessage = ({ hasActiveFilters, resultCount, totalCount }) => {
  if (hasActiveFilters) {
    return resultCount > 0
      ? `Showing ${resultCount} of ${totalCount} knowledge chunk${totalCount === 1 ? '' : 's'}.`
      : 'No knowledge chunks match your current filters.';
  }
  if (totalCount === 0) {
    return 'No knowledge has been generated for this client yet. Upload and process a source to build the Knowledge Base.';
  }
  return `Showing all ${totalCount} knowledge chunk${totalCount === 1 ? '' : 's'} for this client.`;
};

export const parseKnowledgeSearchParams = (searchParams) => {
  const pageRaw = Number.parseInt(searchParams.get('page') || '1', 10);
  const sortDir = searchParams.get('dir') === 'desc' ? 'desc' : 'asc';
  const sortByRaw = searchParams.get('sort') || 'topic';
  return {
    clientId: searchParams.get('client') || '',
    search: searchParams.get('q') || '',
    source: searchParams.get('source') || ALL_SOURCES,
    page: Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : 1,
    sortBy: SORTABLE_COLUMNS.includes(sortByRaw) ? sortByRaw : 'topic',
    sortDir,
  };
};

export const buildKnowledgeSearchParams = ({ clientId, search, source, page, sortBy, sortDir }) => {
  const params = new URLSearchParams();
  if (clientId) params.set('client', clientId);
  if (search) params.set('q', search);
  if (source && source !== ALL_SOURCES) params.set('source', source);
  if (page && page > 1) params.set('page', String(page));
  if (sortBy && sortBy !== 'topic') params.set('sort', sortBy);
  if (sortDir && sortDir !== 'asc') params.set('dir', sortDir);
  return params;
};
